import React from 'react'
import PropTypes from 'prop-types'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import { withStyles, makeStyles, ThemeProvider } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import Paper from '@material-ui/core/Paper'
import Draggable from 'react-draggable'

import { translate } from '../../multilanguage'

const styles = (theme) => ({
  root: {
    margin: 12,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: '0px',
    top: '0px',
    color: theme.palette.grey[500],
  },
})

const primaryStyles = (theme) => ({
  root: {
    padding: '6px 10px 6px 10px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.white,
  },
  closeButton: {
    position: 'absolute',
    right: '0px',
    top: '0px',
    color: theme.palette.white,
  },
})

const useStyles = makeStyles({
  root: {
    width: (props) => props.width,
    borderRadius: '4px'
  }
})

const BaseDialogTitle = props => {
  const { children, classes, onClose, closeX, primaryTitle, ...other } = props
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose && closeX
        ? (
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
          )
        : null}
    </MuiDialogTitle>
  )
}

BaseDialogTitle.propTypes = {
  onClose: PropTypes.func,
  classes: PropTypes.object,
  closeX: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func,
    PropTypes.object
  ]),
  primaryTitle: PropTypes.bool,
}

const DialogTitle = withStyles(styles)(BaseDialogTitle)
const PrimaryDialogTitle = withStyles(primaryStyles)(BaseDialogTitle)

/**
 * PaperComponent component allows the dialog to be moved around from the title block.
 *
 * @component
 * @param {object} props component props
 * @param {string} dialogId dialog id
 * @param {object} styles  Styles for the paper component.
 * @param {boolean} draggable  Makes the dialog draggable.
 * @returns {PaperComponent} PaperComponent component
 */
function PaperComponent(props, dialogId, styles, draggable) {
  const classes = useStyles(styles)
  if (draggable) {
    return (
      <Draggable handle={`#${dialogId}`} cancel={'[class*="MuiDialogContent-root"]'}>
        <Paper classes={classes} {...props} />
      </Draggable>
    )
  }
  return (
    <Paper classes={classes} {...props} />
  )
}

/**
 * BaseDialog component is a dialog used to display information to the user.
 *
 * @component
 * @param {object} props component props
 * @returns {BaseDialog} BaseDialog component
 */
const BaseDialog = props => {
  const classes = useStyles(props)
  const titleStyle = { cursor: props.draggable ? 'move' : 'default' }

  const onClose = (arg, arg2) => {
    if (arg2 === 'backdropClick') {
      return
    }
    props.handleCancel && props.handleCancel()
  }

  return (
    <ThemeProvider theme={innerTheme => typeof props.themeOverride === 'function' ? props.themeOverride(innerTheme) : innerTheme}>
      <Dialog
        ref={props.ref ? props.ref : null}
        open={props.isOpen}
        onClose={onClose}
        PaperComponent={arg => PaperComponent(arg, props.dialogId, props?.paperCustomStyles, props.draggable)}
        maxWidth= {props.maxWidth} // "sm", "xs", "md", "lg", "xl"
        aria-labelledby={props.dialogId}
        id={props.id}
        disableEscapeKeyDown
        disableEnforceFocus
        // fullWidth
      >
        {props.title && !props.primaryTitle && (
          <DialogTitle onClose={props.handleCancel} style={titleStyle} id={props.dialogId} closeX={props.closeX}>
            {typeof props.title === 'function' ? <props.title /> : typeof props.title !== 'string' ? props.title : props.noTranslateTitle ? props.title : translate(props.title)}
          </DialogTitle>
        )}
        {props.title && props.primaryTitle && (
        <PrimaryDialogTitle onClose={props.handleCancel} style={titleStyle} id={props.dialogId} closeX={props.closeX}>
          {typeof props.title === 'function' ? <props.title /> : typeof props.title !== 'string' ? props.title : props.noTranslateTitle ? props.title : translate(props.title)}
        </PrimaryDialogTitle>
        )}
        <DialogContent dividers={props.dividers}>
          {typeof props.message === 'string' ? <DialogContentText>{props.translateMessage ? translate(props.message) : props.message}</DialogContentText> : typeof props.message === 'function' ? <props.message /> : <span>{props.message}</span>}
        </DialogContent>
        {props.buttons && <DialogActions classes={{ root: classes.buttonStyles }}>
          <props.buttons />
        </DialogActions>
        }
      </Dialog>
    </ThemeProvider>
  )
}

BaseDialog.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  dividers: PropTypes.bool,
  closeX: PropTypes.bool,
  ref: PropTypes.any,
  isOpen: PropTypes.bool.isRequired,
  dialogId: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func,
    PropTypes.object
  ]).isRequired,
  handleCancel: PropTypes.func.isRequired,
  themeOverride: PropTypes.func,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func
  ]),
  // eslint-disable-next-line react/no-unused-prop-types
  buttons: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func
  ]),
  paperCustomStyles: PropTypes.object,
  noTranslateTitle: PropTypes.bool,
  translateMessage: PropTypes.bool,
  primaryTitle: PropTypes.bool,
  /**
   * Makes the dialog draggable.
   */
  draggable: PropTypes.bool,
  maxWidth: PropTypes.string
}

export default BaseDialog

